/**
 * SOLID Review
 * S — Strong. One concern: every component-to-component seam of the assembled frontend graph — each
 *     socket married to its lollipop, with the port observer installed. No construction, no logic.
 * O — Good. A new seam is one connector in the method of the component that owns the socket.
 * L — N/A — no inheritance hierarchy.
 * I — Good. One method per phase of `FrontendAssembly`'s sequence.
 * D — Good. The composition root, the one layer permitted to know every component; each Connector<T>
 *     depends only on a port interface.
 */
import { Connector, ProvidedPort, NO_OBSERVER, type IPortObserver } from '@ml3d/kernel';
import type { ComponentFrontendDomain } from '../../domain/uml-components/ComponentFrontendDomain';
import type { ComponentFrontendApplication } from '../../application/uml-components/ComponentFrontendApplication';
import type { ComponentFrontendInfrastructure, IConfigDepContractClients } from '../../infrastructure/uml-components/ComponentFrontendInfrastructure';
import type { ComponentFrontendFramework } from '../uml-components/ComponentFramework';
import type { IPortClientUseCases } from '../../application/ports/ClientUseCases.contract';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IPortGraphQLDomainClient, IPortRestApiClient } from '../../infrastructure/contract/ports';
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPortClientServerEvents } from '../../application/ports/ClientServerEvents.contract';
import type {
  IPortClientState, IPortClientRoundServer, IPortClientRoomServer, IPortClientServerWatch,
  IPortClientTabStorage, IPortClientPlayerServer, IPortClientAuthServer, IPortClientLegalServer,
  IPortClientConsentPlatform, IPortClientPaymentServer, IPortClientAdsServer, IPortClientServerHealth,
  IPortClientMediaServer,
} from '../../application/ports/contracts';

/**
 * THE FRONTEND'S ONE CONNECTOR TABLE (frontend strict-ports plan, FE1d, 2026-10-03) — the twin of the
 * backend's `BackendConnectors`. Every socket of the assembled graph is connected HERE, through
 * `Connector.assemble` (which refuses a label that names a different port), with the observer the
 * composition root installs. Until FE1d the Infrastructure component connected its own wire sockets
 * (`connect()`/`fill()`, §28.77); that rule is overturned on this side too.
 *
 * The Framework's two sockets joined in FE6a: until then the Framework was built per provider mount and
 * connected itself from an adapter registry (with eleven legacy adapter sockets beside them).
 */
export class FrontendConnectors {
  constructor(private readonly observer: IPortObserver = NO_OBSERVER) {}

  /**
   * Infrastructure's sockets — the wire transport and the Application's delivery lollipop — BEFORE
   * `infrastructure.build()`, which reads them. The Application's lollipops exist from its
   * construction, so this runs before either component is built.
   *
   * One Apollo client fills the six GraphQL sockets and one REST client the two REST sockets: each
   * socket is enumerable and breakable on its own, not substitutable on its own.
   */
  infrastructure(infra: ComponentFrontendInfrastructure, app: ComponentFrontendApplication, clients: IConfigDepContractClients): void {
    const wire = infra.requiredPorts;
    new Connector<IPortGraphQLDomainClient>('GraphQLRoomDomain')
      .assemble(wire.graphQLRoomDomainSocket, new ProvidedPort<IPortGraphQLDomainClient>('GraphQLRoomDomain', clients.graphql), this.observer);
    new Connector<IPortGraphQLDomainClient>('GraphQLRoundDomain')
      .assemble(wire.graphQLRoundDomainSocket, new ProvidedPort<IPortGraphQLDomainClient>('GraphQLRoundDomain', clients.graphql), this.observer);
    new Connector<IPortGraphQLDomainClient>('GraphQLPlayerDomain')
      .assemble(wire.graphQLPlayerDomainSocket, new ProvidedPort<IPortGraphQLDomainClient>('GraphQLPlayerDomain', clients.graphql), this.observer);
    new Connector<IPortGraphQLDomainClient>('GraphQLMediaDomain')
      .assemble(wire.graphQLMediaDomainSocket, new ProvidedPort<IPortGraphQLDomainClient>('GraphQLMediaDomain', clients.graphql), this.observer);
    new Connector<IPortGraphQLDomainClient>('GraphQLAdsDomain')
      .assemble(wire.graphQLAdsDomainSocket, new ProvidedPort<IPortGraphQLDomainClient>('GraphQLAdsDomain', clients.graphql), this.observer);
    new Connector<IPortGraphQLDomainClient>('GraphQLPaymentDomain')
      .assemble(wire.graphQLPaymentDomainSocket, new ProvidedPort<IPortGraphQLDomainClient>('GraphQLPaymentDomain', clients.graphql), this.observer);
    new Connector<IPortRestApiClient>('RestAuthApi')
      .assemble(wire.restAuthApiSocket, new ProvidedPort<IPortRestApiClient>('RestAuthApi', clients.rest), this.observer);
    new Connector<IPortRestApiClient>('RestLegalApi')
      .assemble(wire.restLegalApiSocket, new ProvidedPort<IPortRestApiClient>('RestLegalApi', clients.rest), this.observer);
    new Connector<IPortClientServerEvents>('ClientServerEvents')
      .assemble(wire.clientServerEventsSocket, app.providedPorts.clientServerEventsPort, this.observer);
  }

  /**
   * The Application's sockets — the Domain's decisions and Infrastructure's contracted lollipops —
   * AFTER `infrastructure.build()` (the lollipops exist then) and BEFORE `application.build()`.
   */
  application(app: ComponentFrontendApplication, infra: ComponentFrontendInfrastructure, domain: ComponentFrontendDomain): void {
    const sockets = app.requiredPorts;
    const lollipops = infra.providedPorts;
    new Connector<IPortClientDecisions>('ClientDecisions').assemble(sockets.clientDecisionsSocket, domain.clientDecisionsPort, this.observer);
    new Connector<IPortClientState>('ClientState').assemble(sockets.clientStateSocket, lollipops.clientStatePort, this.observer);
    new Connector<IPortClientRoundServer>('ClientRoundServer').assemble(sockets.clientRoundServerSocket, lollipops.clientRoundServerPort, this.observer);
    new Connector<IPortClientRoomServer>('ClientRoomServer').assemble(sockets.clientRoomServerSocket, lollipops.clientRoomServerPort, this.observer);
    new Connector<IPortClientServerWatch>('ClientServerWatch').assemble(sockets.clientServerWatchSocket, lollipops.clientServerWatchPort, this.observer);
    new Connector<IPortClientTabStorage>('ClientTabStorage').assemble(sockets.clientTabStorageSocket, lollipops.clientTabStoragePort, this.observer);
    new Connector<IPortClientPlayerServer>('ClientPlayerServer').assemble(sockets.clientPlayerServerSocket, lollipops.clientPlayerServerPort, this.observer);
    new Connector<IPortClientAuthServer>('ClientAuthServer').assemble(sockets.clientAuthServerSocket, lollipops.clientAuthServerPort, this.observer);
    new Connector<IPortClientLegalServer>('ClientLegalServer').assemble(sockets.clientLegalServerSocket, lollipops.clientLegalServerPort, this.observer);
    new Connector<IPortClientConsentPlatform>('ClientConsentPlatform')
      .assemble(sockets.clientConsentPlatformSocket, lollipops.clientConsentPlatformPort, this.observer);
    new Connector<IPortClientPaymentServer>('ClientPaymentServer')
      .assemble(sockets.clientPaymentServerSocket, lollipops.clientPaymentServerPort, this.observer);
    new Connector<IPortClientAdsServer>('ClientAdsServer').assemble(sockets.clientAdsServerSocket, lollipops.clientAdsServerPort, this.observer);
    new Connector<IPortClientServerHealth>('ClientServerHealth')
      .assemble(sockets.clientServerHealthSocket, lollipops.clientServerHealthPort, this.observer);
    new Connector<IPortClientMediaServer>('ClientMediaServer').assemble(sockets.clientMediaServerSocket, lollipops.clientMediaServerPort, this.observer);
  }

  /**
   * The Framework's two sockets — what React calls and what it renders from — AFTER `application.build()`.
   * The view is by reference (uncontracted, VIEW_BY_REFERENCE); the use cases are guarded at the socket.
   */
  framework(framework: ComponentFrontendFramework, app: ComponentFrontendApplication, infra: ComponentFrontendInfrastructure): void {
    const sockets = framework.requiredPorts;
    new Connector<IPortClientUseCases>('ClientUseCases').assemble(sockets.clientUseCasesSocket, app.providedPorts.clientUseCasesPort, this.observer);
    new Connector<IPortClientStateView>('ClientStateView')
      .assemble(sockets.clientStateViewSocket, infra.providedPorts.clientStateViewPort, this.observer);
  }
}
